/**
 * Plugin delay compensation.
 *
 * A hosted plugin may answer with audio later than it was given it — a
 * look-ahead limiter, a linear-phase EQ — and says how much later in samples.
 * Left alone, a track through such a plugin lands late against the others.
 * The remedy is the one every DAW uses: every route to the output is delayed
 * to the longest, so everything arrives together, a little later than it
 * would have.
 *
 * A track has one route through its output — its own chain, then every bus
 * it feeds on the way to the master, whose own chain delays everything alike
 * and is left out — and one more through each send, into an aux with a chain
 * of its own. Two routes of different lengths cannot be lined up by one delay,
 * so each gets its own. A track's *path* is its chain plus its longest
 * continuation, output or send; what arrives at a track is lined up for that,
 * and each of its shorter continuations is held back to match: the output
 * after the pan, past the post-fader tap, and each send on that send. A leaf
 * — an audio or instrument track — is additionally held before its pre-fader
 * tap, where a pre-fader send is held with the output, by however much its
 * path is shorter than the longest of all. A bus needs no such hold: what
 * reaches it is aligned already.
 *
 * Pure arithmetic over the project's routing; the engine and the render apply
 * it to their own graphs.
 */
import type { Project } from '@/domain/types';

/** How long a compensation delay may be; a plugin asking for more is held here. */
export const MAX_COMPENSATION_SEC = 1;

export interface Compensation {
  /** Before the pre-fader tap: what every route from the track shares. */
  common: Map<string, number>;
  /** After the pan: what the output route still lacks. */
  output: Map<string, number>;
  /** On each send: what that route still lacks. */
  sends: Map<string, Map<string, number>>;
  /** The longest route, which is how late everything now arrives. */
  longestSec: number;
}

const cap = (seconds: number): number => Math.min(MAX_COMPENSATION_SEC, Math.max(0, seconds));

export function compensationFor(project: Project, chainLatencySec: (trackId: string) => number): Compensation {
  const own = (trackId: string): number => cap(chainLatencySec(trackId));
  const path = new Map<string, number>();
  /**
   * Latency from a track's input to the master along its *longest*
   * continuation: its output, or a send into an aux with a longer way to
   * go. What arrives at the track is lined up for that, and the track's
   * shorter continuations are held back to match it.
   */
  const pathFor = (trackId: string, depth = 0): number => {
    const known = path.get(trackId);
    if (known !== undefined) return known;
    const track = project.tracks[trackId];
    if (!track || track.kind === 'master' || depth > 32) return 0;
    let onward = track.outputTrackId ? pathFor(track.outputTrackId, depth + 1) : 0;
    for (const send of track.sends) onward = Math.max(onward, pathFor(send.targetTrackId, depth + 1));
    const total = own(trackId) + onward;
    path.set(trackId, total);
    return total;
  };

  const leaves = project.trackOrder.filter((id) => {
    const track = project.tracks[id];
    return track && (track.kind === 'audio' || track.kind === 'instrument');
  });
  let longest = 0;
  for (const id of leaves) longest = Math.max(longest, pathFor(id));

  const out: Compensation = { common: new Map(), output: new Map(), sends: new Map(), longestSec: longest };
  for (const id of project.trackOrder) {
    const track = project.tracks[id];
    if (!track || track.kind === 'master') continue;
    const leaf = track.kind === 'audio' || track.kind === 'instrument';
    // What arrives here is lined up for this track's longest continuation
    // — for a leaf, once it has been held to the longest route of all.
    const arrivesFor = pathFor(id);
    const shared = leaf ? cap(longest - arrivesFor) : 0;
    const direct = cap(arrivesFor - own(id) - (track.outputTrackId ? pathFor(track.outputTrackId) : 0));
    const perSend = new Map<string, number>();
    for (const send of track.sends) perSend.set(send.id, cap(arrivesFor - own(id) - pathFor(send.targetTrackId)));
    if (shared > 1e-9) out.common.set(id, shared);
    if (direct > 1e-9) out.output.set(id, direct);
    const extras = new Map<string, number>();
    for (const [sendId, delay] of perSend) if (delay > 1e-9) extras.set(sendId, delay);
    if (extras.size > 0) out.sends.set(id, extras);
  }
  return out;
}
